import { createContext, useState, useEffect, useContext } from "react";

import FormulationPickerContext from "./FormulationPickerContext";
import useAxios from "../../utils/useAxios";
import { resObj } from "../../utils/constants";

const FinalLNPSampleContext = createContext();

export default FinalLNPSampleContext;

export const FinalLNPSampleProvider = ({ children }) => {
  const api = useAxios();
  const { 
    formulationsObj, setFormulationsObj, formulationsArray, 
    setFormulationsArray, setFormulationFormErrors
  } = useContext(FormulationPickerContext);

  const initValues = {
    bufferIdArray: [],
  };
  const [initialValues, setInitialValues] = useState(initValues);

  const finalLNPSampleObj = {
    bufferId: null,
    bufferAcronym: '',
    finalPayloadConcentration: '',
    expectedVolumeAfterPurification: '',
    actualVolumeAvailable: '',
  };
  const [finalLNPSampleData, setFinalLNPSampleData] = useState(finalLNPSampleObj);
  const [apiRes, setApiRes] = useState(resObj);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/buffer/list/initial/data/get'
      );
      
      if (response.status === 200) {
        setInitialValues(prev => ({
          ...prev,
          bufferIdArray: response.data,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Final LNP Sample Init Data Get',
        loading: false,
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (formulationsObj?.ids.length === 0) {
      errors['formulation'] = `${formulationsObj?.type} is required!`;
      setFormulationFormErrors(errors);
    }
    if (!finalLNPSampleData?.bufferId) {
      errors['bufferId'] = 'Buffer is required!';
    }
    if (!finalLNPSampleData?.finalPayloadConcentration) {
      errors['finalPayloadConcentration'] = 'Final payload concentration is required!';
    }
    if (!finalLNPSampleData?.expectedVolumeAfterPurification) {
      errors['expectedVolumeAfterPurification'] = 'Volume after purification is required!';
    }

    return errors;
  };

  const handleSubmit = () => {
    setFormErrors(validate());
    setIsSubmit(true);
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      createFinalLNPSample();
    }
  }, [formErrors]);

  useEffect(() => {  
    const bufferAcronym = initialValues?.bufferIdArray.find(
      item => item.unique_id === finalLNPSampleData?.bufferId
    )?.buffer_acronym ?? '';

    setFinalLNPSampleData(prev => ({
      ...prev,
      bufferAcronym: bufferAcronym
    }));
  }, [finalLNPSampleData?.bufferId]);

  const createFinalLNPSample = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post(
        '/api/materials-capture/final-lnp-sample/create', {
        formulation_type: formulationsObj?.type,
        formulation_id_list: formulationsObj?.ids.map(item => +item.id),
        buffer_id: finalLNPSampleData?.bufferId,
        final_payload_concentration: finalLNPSampleData?.finalPayloadConcentration,
        expected_volume_after_purification: finalLNPSampleData?.expectedVolumeAfterPurification,
        actual_volume_available: finalLNPSampleData?.actualVolumeAvailable,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Final LNP Sample create',
        loading: false,
      });
    }
  };

  const contextData = {
    finalLNPSampleData: finalLNPSampleData,
    setFinalLNPSampleData: setFinalLNPSampleData,
    formErrors: formErrors,
    handleSubmit: handleSubmit,
    apiRes: apiRes,
    setApiRes: setApiRes,
    formulationsObj: formulationsObj,
    setFormulationsObj: setFormulationsObj,
    formulationsArray: formulationsArray,
    setFormulationsArray: setFormulationsArray,
    initialValues: initialValues,
  };

  return (
    <FinalLNPSampleContext.Provider value={contextData}>
      {children}
    </FinalLNPSampleContext.Provider>
  )
};